import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  CalendarCheck,
  Calendar as CalendarIcon, ListChecks, Grid3x3, Table as TableIcon,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  MonthView, WeekView, MatrixTableView, TemplateMatrix, FilterBar, ActivityDialog,
  LegendRow,
} from "@/components/publishing/views";

/**
 * Publishing Overview — embeddable panel.
 *
 * Mirrors the PublishingSchedule page layout with Template / Month / Week /
 * Table tabs. When embedded (e.g. inside a scope dashboard for a TL / CM /
 * Department) it aggregates every schedule that falls under the scope.
 *
 * Props (all optional; layer on top of the caller's role scope):
 *   - tlId, managerId, department  → narrowing filters for /publishing-overview
 *   - embedded                     → renders without the outer page padding /
 *                                    background chrome (for Card embedding)
 *   - title, subtitle              → header content
 */
export default function PublishingOverviewPanel({
  tlId, managerId, department, embedded = false,
  title = "Publishing Overview",
  subtitle = "Aggregated publishing calendar",
}) {
  const [data, setData] = useState({ slots: [], schedules: [] });
  const [tab, setTab] = useState("month");
  const [filter, setFilter] = useState("none");
  const [templateGroups, setTemplateGroups] = useState([]);
  const [tplLoading, setTplLoading] = useState(false);
  const [activitySlot, setActivitySlot] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = {};
      if (tlId) params.tl_id = tlId;
      if (managerId) params.manager_id = managerId;
      if (department) params.department = department;
      const r = await api.get("/publishing-overview", { params });
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [tlId, managerId, department]);
  useEffect(() => { load(); }, [load]);

  // Lazy-fetch every schedule's template when the user opens the Template tab.
  // Aggregate templates are grouped by schedule (name shown as section header)
  // so upper hierarchy viewers can see how each TL configured their weekdays.
  useEffect(() => {
    if (tab !== "template" || !data.schedules?.length) return;
    let cancelled = false;
    setTplLoading(true);
    (async () => {
      try {
        const rows = await Promise.all((data.schedules || []).map(async (s) => {
          try {
            const t = await api.get(`/publishing-schedules/${s.id}/template`);
            return { schedule: s, rows: t.data.rows || [] };
          } catch { return { schedule: s, rows: [] }; }
        }));
        if (!cancelled) setTemplateGroups(rows);
      } finally { if (!cancelled) setTplLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [tab, data.schedules]);

  // When an activity dialog opens, refresh that schedule's calendar so the
  // article list reflects latest edits (matches PublishingSchedule behavior).
  const openActivity = useCallback(async (slot) => {
    if (!slot?.schedule_id) { setActivitySlot(slot); return; }
    try {
      const c = await api.get(`/publishing-schedules/${slot.schedule_id}/calendar`);
      const fresh = (c.data.slots || []).find(
        (s) => s.date === slot.date && s.project_id === slot.project_id && s.status === slot.status
      );
      // Preserve schedule_name for the dialog header
      setActivitySlot(fresh ? { ...fresh, schedule_id: slot.schedule_id, schedule_name: slot.schedule_name } : slot);
    } catch { setActivitySlot(slot); }
  }, []);

  // Apply quick filter to slots before passing to views.
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const weekOff = (now.getDay() + 6) % 7;
  const weekStart = new Date(now); weekStart.setDate(now.getDate() - weekOff); weekStart.setHours(0,0,0,0);
  const weekEnd = new Date(weekStart); weekEnd.setDate(weekStart.getDate() + 6); weekEnd.setHours(23,59,59,999);
  const filterSlot = (s) => {
    if (filter === "none") return true;
    if (s.status !== "red") return false;
    const d = new Date(s.date + "T00:00:00");
    if (filter === "missed_m") return d >= monthStart && d <= monthEnd;
    if (filter === "missed_w") return d >= weekStart && d <= weekEnd;
    return true;
  };
  const filteredSlots = (data.slots || []).filter(filterSlot);

  const container = embedded ? "" : "min-h-full bg-slate-50";
  const inner = embedded ? "" : "p-6";
  // Show schedule column in matrix when multiple TL schedules are aggregated
  const showAggregateBadge = (data.schedules?.length || 0) > 1;

  return (
    <div className={container} data-testid="publishing-overview-panel">
      <div className={embedded ? "flex items-center gap-3 mb-3" : "border-b border-slate-200 bg-white px-6 py-3 flex items-center gap-3 sticky top-0 z-30"}>
        <CalendarCheck className="h-5 w-5 text-emerald-500" />
        <h1 className={embedded ? "font-display font-semibold text-slate-900" : "text-xl font-semibold text-slate-900"}>{title}</h1>
        <span className="text-xs text-slate-500 ml-1 truncate">{subtitle}</span>
        {showAggregateBadge && (
          <span className="ml-auto text-[10px] uppercase tracking-wider text-emerald-700 px-2 py-0.5 rounded bg-emerald-100">
            {data.schedules.length} schedules
          </span>
        )}
      </div>

      <div className={inner}>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-white border border-slate-200 mb-4">
            <TabsTrigger value="template" data-testid="overview-tab-template"><ListChecks className="h-4 w-4 mr-1.5" /> Template</TabsTrigger>
            <TabsTrigger value="month" data-testid="overview-tab-month"><CalendarIcon className="h-4 w-4 mr-1.5" /> Month</TabsTrigger>
            <TabsTrigger value="week" data-testid="overview-tab-week"><Grid3x3 className="h-4 w-4 mr-1.5" /> Week</TabsTrigger>
            <TabsTrigger value="table" data-testid="overview-tab-table"><TableIcon className="h-4 w-4 mr-1.5" /> Table</TabsTrigger>
          </TabsList>

          <TabsContent value="template">
            {tplLoading && <div className="text-sm text-slate-400 py-4">Loading templates…</div>}
            {!tplLoading && templateGroups.length === 0 && (
              <div className="text-center py-16 text-sm text-slate-400 bg-white rounded-lg border border-slate-200">
                No publishing schedules in this scope yet.
              </div>
            )}
            {!tplLoading && templateGroups.length > 0 && (
              <div className="space-y-4">
                {templateGroups.map(({ schedule, rows }) => (
                  <TemplateMatrix
                    key={schedule.id}
                    rows={rows}
                    canEdit={false}
                    groupLabel={showAggregateBadge ? `${schedule.tl_name || "Team Lead"} — ${schedule.name}` : schedule.name}
                  />
                ))}
              </div>
            )}
          </TabsContent>
          <TabsContent value="month">
            <FilterBar filter={filter} setFilter={setFilter} testPrefix="overview-filter" />
            <MonthView slots={filteredSlots} onSlotClick={openActivity} />
          </TabsContent>
          <TabsContent value="week">
            <FilterBar filter={filter} setFilter={setFilter} testPrefix="overview-filter" />
            <WeekView slots={filteredSlots} onSlotClick={openActivity} />
          </TabsContent>
          <TabsContent value="table">
            <FilterBar filter={filter} setFilter={setFilter} testPrefix="overview-filter" />
            <MatrixTableView slots={filteredSlots} onSlotClick={openActivity} />
          </TabsContent>
        </Tabs>

        <LegendRow />
      </div>

      {activitySlot && (
        <ActivityDialog slot={activitySlot} onClose={() => setActivitySlot(null)} />
      )}
    </div>
  );
}
